import type { ChatMessage } from './types';

/**
 * 格式化消息时间 (HH:MM)
 */
export function formatMessageTime(dateString: string, locale: string = 'zh-CN'): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString(locale, { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
}

/**
 * 格式化消息日期 (今天/昨天/MM月DD日)
 */
export function formatMessageDate(
  dateString: string, 
  locale: string = 'zh-CN',
  translations?: {
    today: string;
    yesterday: string;
  }
): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return translations?.today || '今天';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return translations?.yesterday || '昨天';
  } else {
    return date.toLocaleDateString(locale, { 
      month: 'long', 
      day: 'numeric' 
    });
  }
}

/**
 * 判断是否应该显示日期分隔符
 */
export function shouldShowDateSeparator(
  currentMessage: ChatMessage,
  previousMessage: ChatMessage | null
): boolean {
  if (!previousMessage) return true;
  
  const currentDate = new Date(currentMessage.created_at).toDateString();
  const previousDate = new Date(previousMessage.created_at).toDateString();
  
  return currentDate !== previousDate;
}

/**
 * 判断消息是否为自己发送
 */
export function isOwnMessage(message: ChatMessage, currentUserId?: string): boolean {
  return message.user_id === currentUserId;
}

/**
 * 获取消息发送者名称
 */
export function getMessageSenderName(
  message: ChatMessage,
  fallbackName: string = '未知用户'
): string {
  return message.user_profile?.display_name || fallbackName;
}

/**
 * 验证消息内容
 */
export function validateMessage(message: string, maxLength: number = 500): {
  valid: boolean;
  error?: string;
} {
  const trimmed = message.trim();
  
  if (!trimmed) {
    return { valid: false, error: '消息内容不能为空' };
  }
  
  if (trimmed.length > maxLength) {
    return { valid: false, error: `消息长度不能超过 ${maxLength} 字符` };
  }
  
  return { valid: true };
}

/**
 * 对消息列表按时间排序
 */
export function sortMessagesByTime(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

/**
 * 合并消息列表（去重）
 */
export function mergeMessages(
  existingMessages: ChatMessage[],
  newMessages: ChatMessage[]
): ChatMessage[] {
  const messageMap = new Map<string, ChatMessage>();
  
  // 先添加现有消息
  existingMessages.forEach(msg => messageMap.set(msg.id, msg));
  
  // 覆盖或添加新消息
  newMessages.forEach(msg => messageMap.set(msg.id, msg));
  
  // 转换为数组并排序
  return sortMessagesByTime(Array.from(messageMap.values()));
}

/**
 * 生成 Realtime channel 名称
 */
export function generateChannelName(groupId: string): string {
  return `room:${groupId}`;
}
